import type { ZodType } from "./zod";
import bundled from '../generated/catalog.json';
import revision from '../generated/revision.json';
import {
  appSchema,
  appAliasSchema,
  categorySchema,
  collectionSchema,
  shortcutSchema,
  subcategorySchema,
  type Catalog,
  type Category,
  type Shortcut,
  type Subcategory,
  type SupportedApp,
} from "./types";

export const APP_ALIASES: Readonly<Record<string, string>> = {
  "amazon-shopping-made-easy": "amazon",
  "arc-search-find-it-faster": "arc-search",
  "drafts-5-capture-act": "drafts",
  "google-maps-transit-food": "google-maps",
  "perplexity-ask-anything": "perplexity",
  "reddit-trending-news": "reddit",
  "soundcloud-music-audio": "soundcloud",
  "spotify-music": "spotify",
  "telegram-messenger": "telegram",
  "threads-an-instagram-app": "threads",
  "things-3": "things",
  "tiktok-including-musically": "tiktok",
  "twitch-live-game-streaming": "twitch",
  "venmo-send-receive-money": "venmo",
  "whatsapp-messenger": "whatsapp",
  "waze-navigation-live-traffic": "waze",
  "youtube-watch-listen-stream": "youtube",
};

const canonicalAppId = (id: string, aliases: Readonly<Record<string, string>>) => aliases[id] ?? id;

function parseRows<T>(schema: ZodType<T>, rows: unknown[]): T[] {
  return rows.flatMap((row) => {
    const result = schema.safeParse(row);
    return result.success ? [result.data] : [];
  });
}

async function fetchAll(table: string): Promise<unknown[]> {
  const { supabase } = await import('./supabase');
  if (!supabase) throw new Error("Supabase is not configured");
  const pageSize = 1000;
  const rows: unknown[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await supabase
      .from(table)
      .select("*").order(table === 'content_app_aliases' ? 'alias_id' : 'id')
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const page = data ?? [];
    rows.push(...page);
    if (page.length < pageSize) break;
  }
  return rows;
}

async function fetchAppAliases(): Promise<unknown[]> {
  try {
    return await fetchAll("content_app_aliases");
  } catch {
    return [];
  }
}

async function loadSupabaseCatalog(): Promise<Catalog> {
  const [categories, subcategories, apps, shortcuts, collections, aliasRows] =
    await Promise.all([
      fetchAll("content_categories"),
      fetchAll("content_subcategories"),
      fetchAll("content_apps"),
      fetchAll("content_templates"),
      fetchAll("content_collections"),
      fetchAppAliases(),
    ]);
  const remoteAliases = Object.fromEntries(
    parseRows(appAliasSchema, aliasRows).map((alias) => [alias.alias_id, alias.canonical_app_id]),
  );
  return normalizeCatalog({
    categories: parseRows(categorySchema, categories),
    subcategories: parseRows(subcategorySchema, subcategories),
    apps: parseRows(appSchema, apps),
    shortcuts: parseRows(shortcutSchema, shortcuts),
    collections: parseRows(collectionSchema, collections),
    appAliases: { ...APP_ALIASES, ...remoteAliases },
    source: "supabase",
  });
}

function normalizeCatalog(catalog: Catalog): Catalog {
  const shortcuts = catalog.shortcuts.map((shortcut) => ({
    ...shortcut,
    app_ids: [...new Set(shortcut.app_ids.map((id) => canonicalAppId(id, catalog.appAliases)))],
  }));
  const appCounts = new Map<string, number>();
  const topicCounts = new Map<string, number>();
  for (const shortcut of shortcuts) {
    for (const appId of new Set(shortcut.app_ids)) {
      appCounts.set(appId, (appCounts.get(appId) ?? 0) + 1);
    }
    for (const topicId of new Set(shortcut.subcategory_ids)) {
      topicCounts.set(topicId, (topicCounts.get(topicId) ?? 0) + 1);
    }
  }
  const subcategories = catalog.subcategories.filter(
    (topic) => (topicCounts.get(topic.id) ?? 0) > 0,
  );
  const categoryIds = new Set(subcategories.map((topic) => topic.parent_category_id));
  return {
    ...catalog,
    shortcuts,
    subcategories,
    categories: catalog.categories
      .filter((category) => categoryIds.has(category.id))
      .sort((a, b) => a.priority - b.priority),
    apps: catalog.apps
      .filter((app) => !catalog.appAliases[app.id] && (appCounts.get(app.id) ?? 0) > 0)
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}

const initial = normalizeCatalog({categories:parseRows(categorySchema,bundled.categories),subcategories:parseRows(subcategorySchema,bundled.subcategories),apps:parseRows(appSchema,bundled.apps),shortcuts:parseRows(shortcutSchema,bundled.shortcuts),collections:parseRows(collectionSchema,bundled.collections),source:'bundled',appAliases:{...APP_ALIASES,...bundled.appAliases}});
let current = initial;
const listeners = new Set<() => void>();
export const getCatalog = () => current;
export const getBundledCatalog = () => initial;
export function subscribeCatalog(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
function publish(catalog: Catalog) { current = catalog; listeners.forEach(listener => listener()); }
let started = false;
let checking = false;
let version = revision.version;
let lastCheck = 0;
const cacheKey = 'shortcut-library-catalog-v3';
function database(): Promise<IDBDatabase> {
 return new Promise((resolve,reject) => { const request=indexedDB.open(cacheKey,1);request.onupgradeneeded=()=>request.result.createObjectStore('catalog');request.onsuccess=()=>resolve(request.result);request.onerror=()=>reject(request.error); });
}
async function cachedSnapshot() {
 const db=await database();
 try { return await new Promise<{version:number;catalog:Catalog}|undefined>((resolve,reject)=>{const r=db.transaction('catalog').objectStore('catalog').get('current');r.onsuccess=()=>resolve(r.result);r.onerror=()=>reject(r.error);}); } finally {db.close();}
}
async function persist(catalog:Catalog,nextVersion:number) {
 const db=await database();
 try {await new Promise<void>((resolve,reject)=>{const tx=db.transaction('catalog','readwrite');tx.objectStore('catalog').put({version:nextVersion,catalog},'current');tx.oncomplete=()=>resolve();tx.onerror=()=>reject(tx.error);});}finally{db.close();}
}
export async function checkCatalogUpdates() {
 if(checking || Date.now()-lastCheck<60_000 || !navigator.onLine) return;
 checking=true;lastCheck=Date.now();
 try {
   const {supabase}=await import('./supabase');if(!supabase)return;
   const {data,error}=await supabase.from('data_version').select('version').eq('id','current').single();
   if(error || !data || data.version<=version)return;
   const updated=await loadSupabaseCatalog();
   const confirmation=await supabase.from('data_version').select('version').eq('id','current').single();
   if(confirmation.error||confirmation.data?.version!==data.version)throw new Error('Catalog changed during synchronization');
   if(!updated.shortcuts.length)throw new Error('Empty catalog update');
   // Persist and publish one complete snapshot, never a partially fetched page.
   version=data.version;publish(updated);await persist(updated,version);
 }catch{/* Existing bundled/cached content stays available during service or storage failures. */}
 finally{checking=false;}
}
export function startCatalogSync() {
 if(started || typeof window==='undefined')return;started=true;
 const run=async()=>{try{const cached=await cachedSnapshot();if(cached?.catalog?.shortcuts?.length && Number.isFinite(cached.version)&&cached.version>=revision.version){version=cached.version;publish(normalizeCatalog(cached.catalog));}}catch{/* Private browsing or unavailable storage. */}void checkCatalogUpdates();};
 if('requestIdleCallback' in window)window.requestIdleCallback(()=>void run(),{timeout:2500});else setTimeout(()=>void run(),500);
 document.addEventListener('visibilitychange',()=>{if(document.visibilityState==='visible')void checkCatalogUpdates();});
 window.addEventListener('online',()=>void checkCatalogUpdates());
}
export async function loadCatalog():Promise<Catalog>{return current;}

export function shortcutCountForApp(catalog: Catalog, appId: string): number {
  const canonical = canonicalAppId(appId, catalog.appAliases);
  return catalog.shortcuts.filter((shortcut) => shortcut.app_ids.includes(canonical)).length;
}

export function shortcutsForApp(catalog: Catalog, appId: string): Shortcut[] {
  const canonical = canonicalAppId(appId, catalog.appAliases);
  return catalog.shortcuts.filter((shortcut) => shortcut.app_ids.includes(canonical));
}

export function shortcutsForCategory(catalog: Catalog, categoryId: string): Shortcut[] {
  const topicIds = new Set(
    catalog.subcategories
      .filter((topic) => topic.parent_category_id === categoryId)
      .map((topic) => topic.id),
  );
  return catalog.shortcuts.filter((shortcut) =>
    shortcut.subcategory_ids.some((id) => topicIds.has(id)),
  );
}

export function categoryForShortcut(catalog: Catalog, shortcut: Shortcut): Category | undefined {
  const topic = catalog.subcategories.find(
    (candidate) => candidate.id === shortcut.primary_subcategory_id,
  );
  return catalog.categories.find((category) => category.id === topic?.parent_category_id);
}

export function appsForShortcut(catalog: Catalog, shortcut: Shortcut): SupportedApp[] {
  const ids = new Set(shortcut.app_ids.map((id) => canonicalAppId(id, catalog.appAliases)));
  return catalog.apps.filter((app) => ids.has(app.id));
}

export function topicsForCategory(catalog: Catalog, categoryId: string): Subcategory[] {
  return catalog.subcategories.filter((topic) => topic.parent_category_id === categoryId);
}
